/* Educational comparisons only. Shopify's main variant picker owns purchase state. */
if (!customElements.get('ring-diamond-guide')) {
  const metadataCache = new Map();
  const shapeLabels = {
    ROUND: 'Round', OVAL: 'Oval', EMERALD: 'Emerald', PEAR: 'Pear', PRINCESS: 'Princess',
    CUSHION: 'Cushion', RADIANT: 'Radiant', MARQUISE: 'Marquise', ASSCHER: 'Asscher',
    HEART: 'Heart', BAGUETTE: 'Baguette', OLD_MINER: 'Old mine cut'
  };
  // Typical face-up length and width in millimetres at 1.00 ct. Both scale with the
  // cube root of carat weight. `prongs` are claw positions as percentages of the stone box.
  const ROUND_MM = 6.5;
  const shapeProfiles = {
    ROUND: { length: 6.5, width: 6.5, prongs: [[15, 15], [85, 15], [15, 85], [85, 85]] },
    OVAL: { length: 7.7, width: 5.7, prongs: [[16, 14], [84, 14], [16, 86], [84, 86]] },
    EMERALD: { length: 7, width: 5, prongs: [[7, 5], [93, 5], [7, 95], [93, 95]] },
    PEAR: { length: 8.5, width: 5.5, prongs: [[50, 2], [9, 58], [91, 58], [24, 91], [76, 91]] },
    PRINCESS: { length: 5.5, width: 5.5, prongs: [[5, 5], [95, 5], [5, 95], [95, 95]] },
    CUSHION: { length: 5.6, width: 5.5, prongs: [[10, 10], [90, 10], [10, 90], [90, 90]] },
    RADIANT: { length: 6.5, width: 4.8, prongs: [[7, 5], [93, 5], [7, 95], [93, 95]] },
    MARQUISE: { length: 10, width: 5, prongs: [[50, 1.5], [50, 98.5], [10, 34], [90, 34], [10, 66], [90, 66]] },
    ASSCHER: { length: 5.5, width: 5.5, prongs: [[7, 7], [93, 7], [7, 93], [93, 93]] },
    HEART: { length: 6.5, width: 6.5, prongs: [[25, 6], [75, 6], [5, 42], [95, 42], [50, 98]] },
    BAGUETTE: { length: 10, width: 4, prongs: [[8, 3], [92, 3], [8, 97], [92, 97]] },
    OLD_MINER: { length: 5.9, width: 5.7, prongs: [[10, 10], [90, 10], [10, 90], [90, 90]] }
  };
  const metalFamily = name => {
    const lower = String(name || '').toLowerCase();
    return lower.includes('platinum') ? 'platinum' :
      lower.includes('rose') ? 'rose' :
      lower.includes('yellow') ? 'yellow' :
      lower.includes('white') ? 'white' : null;
  };
  customElements.define('ring-diamond-guide', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      const start = () => {
        if (!this.isConnected || signal.aborted) return;
        this.initialise(signal);
      };

      // A deferred asset and Theme Editor replacements both have complete children.
      // Also support an element encountered while the initial document is parsing.
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', start, { once: true, signal });
      } else {
        start();
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    initialise(signal) {
      this.addEventListener('change', event => {
        const control = event.target;
        if (!(control instanceof Element)) return;
        if (control.matches('[data-grade-control]:checked')) this.updateGrade(control);
        if (control.matches('[data-shape-select]')) this.updateShape(control.value);
      }, { signal });

      this.addEventListener('input', event => {
        if (event.target instanceof HTMLInputElement && event.target.matches('[data-carat-range]')) {
          this.updateCarat(event.target);
        }
      }, { signal });

      this.addEventListener('click', event => {
        const preset = event.target instanceof Element ? event.target.closest('[data-carat-preset]') : null;
        const range = this.querySelector('[data-carat-range]');
        if (!preset || !this.contains(preset) || !range) return;
        const value = Number(preset.dataset.caratPreset);
        if (!Number.isFinite(value)) return;
        range.value = Math.min(Number(range.max), Math.max(Number(range.min), value));
        this.updateCarat(range);
      }, { signal });

      const stopUnavailableNavigation = event => {
        const link = event.target instanceof Element ? event.target.closest('[data-guide-diamond]') : null;
        if (link?.getAttribute('aria-disabled') === 'true') event.preventDefault();
      };
      this.addEventListener('click', stopUnavailableNavigation, { signal });
      this.addEventListener('auxclick', stopUnavailableNavigation, { signal });

      // Delegate to the document because native section renders can replace the form.
      document.addEventListener('variant:change', event => {
        if (!this.isMainForm(event.target)) return;
        this.syncVariant(event.detail?.variant);
      }, { signal });

      document.addEventListener('change', event => {
        const control = event.target;
        if (control instanceof HTMLInputElement && control.matches('[data-option-position]') && this.isMainForm(control.form)) {
          this.setLinkAvailability(false, true);
        }
      }, { signal });

      // When the merchant reloads the product section, recover its new selected variant.
      document.addEventListener('shopify:section:load', () => this.syncCurrentVariant(), { signal });

      this.addEventListener('shopify:block:select', event => {
        const control = event.target instanceof Element ? event.target.closest('.dg-grade')?.querySelector('[data-grade-control]') : null;
        if (!control || control.disabled) return;
        control.checked = true;
        this.updateGrade(control);
      }, { signal });

      ['colour', 'clarity'].forEach(family => {
        const controls = [...this.querySelectorAll('[data-grade-control]')].filter(control =>
          control.dataset.family === family && !control.disabled);
        const selected = controls.find(control => control.checked) || controls[0];
        if (!selected) return;
        selected.checked = true;
        this.updateGrade(selected);
      });
      const range = this.querySelector('[data-carat-range]');
      if (range) {
        this.renderCaratPresets(range);
        this.updateCarat(range);
      }
      const directShapes = this.normaliseShapes(this.dataset.supportedShapes);
      if (directShapes.length) {
        this.applySupportedShapes(directShapes);
      } else {
        this.dataset.supportedShapes = '';
        this.querySelectorAll('[data-shape-label]').forEach(label => {
          label.textContent = label.dataset.exampleLabel || 'Explore example shapes';
        });
      }
      try {
        this.stoneSources = JSON.parse(this.querySelector('script[data-stone-sources]')?.textContent || '{}');
      } catch {
        this.stoneSources = {};
      }
      this.updateShape(this.querySelector('[data-shape-select]')?.value || 'ROUND');
      this.syncCurrentVariant();
      this.setAttribute('data-ready', '');
      this.loadSettingMetadata(signal);
    }

    async loadSettingMetadata(signal) {
      const missingRange = this.dataset.rangeSource === 'example';
      const missingShapes = !this.dataset.supportedShapes?.trim();
      if (this.dataset.useNivoda !== 'true' || (!missingRange && !missingShapes) || !/^\d+$/.test(this.dataset.productId || '')) return;
      const language = document.documentElement.lang || 'en';
      const country = this.dataset.country || undefined;
      const cacheKey = `${this.dataset.productId}:${language}:${country || ''}`;
      const cached = metadataCache.get(cacheKey);
      if (cached && Date.now() - cached.fetchedAt < 300000) {
        this.applySettingMetadata(cached.product);
        return;
      }

      const request = new AbortController();
      const cancel = () => request.abort();
      signal.addEventListener('abort', cancel, { once: true });
      const timeout = setTimeout(cancel, 8000);
      try {
        const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
        const response = await fetch(`${root}apps/diamond-proxy/api/setting-metafields`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ gid: `gid://shopify/Product/${this.dataset.productId}`, language, country }),
          signal: request.signal
        });
        if (!response.ok) return;
        const payload = await response.json();
        const product = payload?.data?.product;
        if (!product || signal.aborted || request.signal.aborted || !this.isConnected) return;
        metadataCache.set(cacheKey, { product, fetchedAt: Date.now() });
        this.applySettingMetadata(product);
      } catch {
        // App access can be unavailable in a preview. Clearly labelled examples remain useful.
      } finally {
        clearTimeout(timeout);
        signal.removeEventListener('abort', cancel);
      }
    }

    applySettingMetadata(product) {
      const min = Number(product.caratsMin?.value);
      const max = Number(product.caratsMax?.value);
      const range = this.querySelector('[data-carat-range]');
      if (this.dataset.rangeSource === 'example' && range && Number.isFinite(min) && Number.isFinite(max) && min > 0 && min <= max) {
        const previousValue = Number(range.value);
        range.min = min;
        range.max = max;
        range.value = Math.min(max, Math.max(min, Number.isFinite(previousValue) ? previousValue : min));
        range.disabled = min === max;
        this.dataset.rangeSource = 'product';
        this.querySelectorAll('[data-range-min]').forEach(output => { output.textContent = `${this.formatCarat(min)} ct`; });
        this.querySelectorAll('[data-range-max]').forEach(output => { output.textContent = `${this.formatCarat(max)} ct`; });
        this.querySelectorAll('[data-range-source]').forEach(caption => {
          caption.dataset.rangeSource = 'product';
          caption.textContent = `${caption.dataset.productLabel || 'Carat range for this setting'} · ${this.formatCarat(min)}–${this.formatCarat(max)} ct`;
        });
        this.renderCaratPresets(range);
        this.updateCarat(range);
      }

      if (this.dataset.supportedShapes?.trim()) return;
      this.applySupportedShapes(this.normaliseShapes(product.supportedShapes?.value));
    }

    normaliseShapes(raw) {
      let codes = Array.isArray(raw) ? raw : typeof raw === 'string' ? raw.split(',') : [];
      if (typeof raw === 'string' && raw.trim().startsWith('[')) {
        try {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) codes = parsed;
        } catch { /* Unrecognised entries remain illustrative examples. */ }
      }
      return [...new Set(codes.map(code => {
        const normalised = String(code).trim().toUpperCase().replace(/[\s-]+/g, '_');
        if (normalised.startsWith('CUSHION_')) return 'CUSHION';
        if (normalised === 'SQUARE_EMERALD') return 'ASSCHER';
        if (normalised === 'SQUARE_RADIANT') return 'RADIANT';
        return normalised;
      }).filter(code => Object.hasOwn(shapeLabels, code)))];
    }

    applySupportedShapes(shapes) {
      const select = this.querySelector('[data-shape-select]');
      if (!shapes.length || !select) return;
      const previousShape = select.value;
      select.replaceChildren(...shapes.map(shape => new Option(shapeLabels[shape], shape)));
      select.value = shapes.includes(previousShape) ? previousShape : shapes[0];
      this.dataset.supportedShapes = shapes.join(',');
      this.querySelectorAll('[data-shape-label]').forEach(label => {
        label.textContent = label.dataset.supportedLabel || 'Shapes for this setting';
      });
      this.updateShape(select.value);
    }

    formatCarat(value) {
      return value.toLocaleString(document.documentElement.lang || 'en-GB', {
        minimumFractionDigits: 1, maximumFractionDigits: 2
      });
    }

    renderCaratPresets(range) {
      const containers = [...this.querySelectorAll('[data-carat-presets]')];
      if (!containers.length) return;
      const min = Number(range.min);
      const max = Number(range.max);
      if (!Number.isFinite(min) || !Number.isFinite(max) || min <= 0 || min > max) {
        containers.forEach(container => container.replaceChildren());
        return;
      }

      // Use familiar half-carat steps, but retain smaller settings' real bounds.
      // Both endpoints stay available even when they fall between regular steps.
      let step = max - min >= 0.5 ? 0.5 : 0.1;
      step *= Math.max(1, Math.ceil((max - min) / (step * 6)));
      const values = [min];
      const first = Math.floor(min / step) + 1;
      const last = Math.ceil(max / step) - 1;
      for (let index = first; index <= last; index++) {
        const value = Number((index * step).toFixed(6));
        if (value > min && value < max) values.push(value);
      }
      if (max > min) values.push(max);
      const presets = values.length <= 7 ? values :
        Array.from({ length: 7 }, (_, index) => values[Math.round(index * (values.length - 1) / 6)]);

      containers.forEach(container => {
        container.replaceChildren(...presets.map(value => {
          const button = document.createElement('button');
          button.type = 'button';
          button.dataset.caratPreset = String(value);
          button.textContent = this.formatCarat(value);
          button.setAttribute('aria-label', `${this.formatCarat(value)} carats`);
          button.setAttribute('aria-pressed', 'false');
          return button;
        }));
      });
    }

    updateGrade(control) {
      const family = control.dataset.family;
      if (family !== 'colour' && family !== 'clarity') return;
      this.querySelectorAll(`[data-${family}-value]`).forEach(output => {
        output.textContent = control.value;
      });
      this.querySelectorAll('[data-grade-copy]').forEach(panel => {
        if (panel.dataset.family === family) panel.hidden = panel.dataset.grade !== control.value;
      });

      let hasAssignedImage = false;
      this.querySelectorAll('[data-grade-image]').forEach(image => {
        if (image.dataset.family !== family) return;
        const selected = image.dataset.grade === control.value;
        image.hidden = !selected;
        hasAssignedImage ||= selected;
      });
      this.querySelectorAll('[data-grade-default]').forEach(illustration => {
        if (illustration.dataset.family !== family) return;
        illustration.toggleAttribute('hidden', hasAssignedImage);
        illustration.style.display = hasAssignedImage ? 'none' : '';
      });

      if (family === 'colour' && /^#[0-9a-f]{3,8}$/i.test(control.dataset.tint || '')) {
        this.style.setProperty('--dg-tint', control.dataset.tint);
      }
      if (family === 'clarity') {
        const parsedLevel = Number(control.dataset.level);
        const level = Number.isFinite(parsedLevel) ? Math.max(0, Math.min(7, parsedLevel)) : 0;
        this.style.setProperty('--dg-inclusion-opacity', String(0.25 + level * 0.08));
        this.querySelectorAll('[data-inclusion-level]').forEach(mark => {
          const markLevel = Number(mark.dataset.inclusionLevel);
          const visible = Number.isFinite(markLevel) && markLevel <= level;
          mark.toggleAttribute('hidden', !visible);
          mark.style.display = visible ? '' : 'none';
        });
      }
    }

    updateCarat(range) {
      const value = Number(range.value);
      if (!Number.isFinite(value) || value <= 0) return;
      const min = Number(range.min);
      const max = Number(range.max);
      const carat = Math.min(Number.isFinite(max) && max > 0 ? max : value,
        Math.max(Number.isFinite(min) && min > 0 ? min : value, value));
      const formatted = this.formatCarat(carat);
      this.querySelectorAll('[data-carat-value]').forEach(output => { output.textContent = `${formatted} ct`; });
      range.setAttribute('aria-valuetext', `${formatted} carats`);
      this.querySelectorAll('[data-carat-preset]').forEach(button => {
        button.setAttribute('aria-pressed', String(Math.abs(Number(button.dataset.caratPreset) - carat) < 0.000001));
      });
      const scale = String(Math.cbrt(carat));
      this.style.setProperty('--dg-stone-scale', scale);
      this.style.setProperty('--dg-carat-scale', scale);
    }

    updateShape(shape) {
      const requested = String(shape).toUpperCase();
      const selected = Object.hasOwn(shapeProfiles, requested) ? requested : 'ROUND';
      const profile = shapeProfiles[selected];
      this.style.setProperty('--dg-shape-w', String(profile.width / ROUND_MM));
      this.style.setProperty('--dg-shape-ratio', String(profile.width / profile.length));
      this.querySelectorAll('[data-stone-image]').forEach(image => {
        const source = this.stoneSources?.[selected] || this.stoneSources?.ROUND;
        if (source && image.getAttribute('src') !== source) image.setAttribute('src', source);
        image.dataset.shape = selected;
      });
      this.querySelectorAll('[data-stone-prongs]').forEach(holder => {
        holder.replaceChildren(...profile.prongs.map(([x, y]) => {
          const prong = document.createElement('span');
          prong.style.left = `${x}%`;
          prong.style.top = `${y}%`;
          return prong;
        }));
      });
    }

    swapHandImage(family) {
      this.querySelectorAll('[data-hand-image]').forEach(image => {
        const key = `hand${family[0].toUpperCase()}${family.slice(1)}`;
        const source = image.dataset[key] || image.dataset.handWhite;
        if (!source || image.getAttribute('src') === source) return;
        // Keep the current photo on screen until the replacement has loaded.
        const next = new Image();
        next.onload = () => { if (this.isConnected) image.src = source; };
        next.src = source;
      });
    }

    isMainForm(form) {
      return Boolean(this.dataset.productId && form instanceof HTMLFormElement &&
        form.getAttribute('id')?.startsWith(`product-form-main-${this.dataset.productId}-`));
    }

    syncCurrentVariant() {
      const form = [...document.forms].find(candidate => this.isMainForm(candidate));
      const picker = form && [...document.querySelectorAll('variant-picker[form-id]')].find(candidate =>
        candidate.getAttribute('form-id') === form.getAttribute('id'));
      // The native getter has the most recent state, including an unavailable combination.
      if (picker && 'selectedVariant' in picker && picker.selectedVariant !== undefined) {
        this.syncVariant(picker.selectedVariant);
        return;
      }
      const source = picker?.querySelector('script[data-variant]') || this.querySelector('script[data-guide-variant]');
      if (!source) return;
      try {
        this.syncVariant(JSON.parse(source.textContent));
      } catch {
        // Malformed optional data should not disable the educational controls.
        this.setLinkAvailability(false);
      }
    }

    syncVariant(variant) {
      this.querySelectorAll('[data-guide-diamond]').forEach(link => {
        if (!variant?.id) return;
        try {
          const url = new URL(link.getAttribute('href'), document.baseURI);
          url.searchParams.set('settingVariantId', variant.id);
          link.href = url.toString();
        } catch {
          // Keep merchant-provided markup intact if an optional link is invalid.
        }
      });
      this.setLinkAvailability(Boolean(variant?.id && variant.available));

      const index = Number(this.dataset.metalOption);
      const metal = Number.isInteger(index) && index >= 0 ? variant?.options?.[index] : null;
      if (!metal) return;
      this.querySelectorAll('[data-current-metal]').forEach(output => { output.textContent = metal; });
      const family = metalFamily(metal);
      if (!family) return;
      const tint = { platinum: this.dataset.platinum, rose: this.dataset.roseGold,
        yellow: this.dataset.yellowGold, white: this.dataset.whiteGold }[family];
      if (tint && /^#[0-9a-f]{3,8}$/i.test(tint)) this.style.setProperty('--dg-metal', tint);
      this.swapHandImage(family);
    }

    setLinkAvailability(available, pending = false) {
      this.querySelectorAll('[data-guide-diamond]').forEach(link => {
        link.setAttribute('aria-disabled', String(!available));
        link.toggleAttribute('data-variant-pending', pending);
        if (pending) link.setAttribute('aria-busy', 'true'); else link.removeAttribute('aria-busy');
        if (available) link.removeAttribute('tabindex'); else link.tabIndex = -1;
      });
    }
  });
}
